import React, { ChangeEvent, FormEvent, useCallback, useState } from 'react';

import { useSelector } from 'react-redux';

import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';

import * as EC from '@luxuryescapes/contract-svc-experience';

import ErrorDisplay from '~/components/Common/ErrorDisplay';

import {
  DEAL_DESCRIPTION,
  IMAGES,
  INCLUDED,
  SHORT_DESCRIPTION,
  THINGS_TO_KNOW,
  TITLE,
} from '~/consts/experiences/offer';

import { ExperienceOffer, getExperienceOfferById } from '~/services/ExperiencesService';

export interface EditableOfferDetails {
  title?: string;
  images?: Array<EC.Experiences.Image>;
  thingsToKnowNew?: EC.Experiences.ThingsToKnow;
  dealDescription?: string;
  shortDescription?: string;
  included?: string;
}
interface Props {
  isCopyModalVisible: boolean;
  hideCopyModal: () => void;
  originalOfferDetails: ExperienceOffer;
  copyOfferData: (editableOfferDetails: EditableOfferDetails, selectedCopyOptions: Array<string>) => void;
}

const ALLOWED_COPY_OPTIONS = [TITLE, IMAGES, THINGS_TO_KNOW, DEAL_DESCRIPTION, SHORT_DESCRIPTION, INCLUDED];

const COPY_OPTIONS_LABELS = {
  [TITLE]: 'Title',
  [IMAGES]: 'Images',
  [THINGS_TO_KNOW]: 'Things to know',
  [DEAL_DESCRIPTION]: 'Deal description',
  [SHORT_DESCRIPTION]: 'Short description',
  [INCLUDED]: 'Inclusions',
};

function ExperienceOfferContentCopyModal({
  isCopyModalVisible,
  hideCopyModal,
  originalOfferDetails,
  copyOfferData,
}: Props) {
  const [copyOfferId, setCopyOfferId] = useState('');
  const [selectedCopyOptions, setSelectedCopyOptions] = useState<Array<string>>([]);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOfferIdChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCopyOfferId(event.target.value);
  };

  const handleToggleCopyOption = (event: ChangeEvent<HTMLInputElement>) => {
    if (selectedCopyOptions.includes(event.target.name)) {
      setSelectedCopyOptions((prev) => prev.filter((option) => option !== event.target.name));
    } else {
      setSelectedCopyOptions((prev) => [...prev, event.target.name]);
    }
  };

  const tenant = useSelector((state: App.State) => state.tenant);

  const getEditableOfferDetails = (copiedOffer: ExperienceOffer) => {
    const editableOfferDetails = {};
    const editableFields = [];
    selectedCopyOptions.forEach((option) => {
      if (copiedOffer[option]) {
        editableFields.push(option);
      }
    });

    for (const field of editableFields) {
      editableOfferDetails[field] = copiedOffer[field];
    }

    return editableOfferDetails;
  };

  const checkOffersCompatibility = (copiedOffer: ExperienceOffer, currentOfferStatus: string) => {
    let areOffersCompatible = true;
    let errorMessage = '';

    if (areOffersCompatible && currentOfferStatus === 'ONLINE') {
      areOffersCompatible = false;
      errorMessage = "You can't overwrite the details of a content-approved offer";
    }

    return {
      areOffersCompatible,
      errorMessage: errorMessage,
    };
  };

  const fetchOfferExperienceById = useCallback(async (): Promise<ExperienceOffer | null> => {
    try {
      const res = await getExperienceOfferById({
        id: copyOfferId,
        brand: tenant.brand,
        curationData: true,
      });
      if (res?.errors?.length > 0) {
        console.error('[ERROR] Experience offer was not found.');
        setErrorMessage(res?.errors[0]);
      }

      return res.result;
    } catch (error) {
      console.error('ERROR ON FETCH EXPERIENCE OFFER: ', error);
      setErrorMessage(error.message);
    }
  }, [copyOfferId, tenant.brand]);

  const searchAndCopyOfferDetails = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setHasError(false);
    setErrorMessage('');
    setCopySuccess(false);
    setIsLoading(true);

    const copyOffer = await fetchOfferExperienceById();

    if (!copyOffer) {
      setHasError(true);
      setErrorMessage('There is no such offer');
      setIsLoading(false);
      return;
    }

    const check = checkOffersCompatibility(copyOffer, originalOfferDetails.status);

    if (!check.areOffersCompatible) {
      setHasError(true);
      setErrorMessage(check.errorMessage);
      setIsLoading(false);
      return;
    }

    // get and pass just the details that are editable
    const editableOfferDetails = getEditableOfferDetails(copyOffer);
    copyOfferData(editableOfferDetails, selectedCopyOptions);
    setCopySuccess(true);
    setIsLoading(false);
  };

  return (
    <Dialog open={isCopyModalVisible} onClose={hideCopyModal}>
      <DialogTitle>Copy editable details from another content-approved offer</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2">
          Please note that this action will pre-fill the details and will let you review and save the result. If you
          have data in any of the editable fields, it will be overwritten.
        </Typography>

        <form onSubmit={searchAndCopyOfferDetails.bind(this)}>
          <FormGroup>
            {hasError && <ErrorDisplay message={errorMessage} />}
            {copySuccess && <Alert severity="success">Offer details copied successfully</Alert>}
            <Grid container spacing={2} alignItems="center">
              <Grid
                size={{
                  md: 'grow',
                }}
              >
                <TextField
                  type="text"
                  placeholder="Type offer ID here"
                  value={copyOfferId}
                  onChange={handleOfferIdChange}
                  fullWidth
                />
              </Grid>
              <Grid>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={selectedCopyOptions.length === 0 || isLoading}
                >
                  {isLoading ? 'Copying...' : 'Copy'}
                </Button>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              {ALLOWED_COPY_OPTIONS.map((option) => (
                <Grid
                  key={option}
                  size={{
                    sm: 4,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        name={option}
                        id={`cp-option-${option}`}
                        checked={selectedCopyOptions.includes(option)}
                        onChange={handleToggleCopyOption}
                      />
                    }
                    label={`Copy ${COPY_OPTIONS_LABELS[option]}`}
                  />
                </Grid>
              ))}
            </Grid>
            {selectedCopyOptions.includes(IMAGES) &&
              originalOfferDetails.images &&
              originalOfferDetails.images.length > 0 && (
                <Alert severity="warning">
                  This offer currently has images, continuing will <strong>replace </strong> these with the copied
                  images
                </Alert>
              )}
          </FormGroup>
        </form>
      </DialogContent>
      <DialogActions>
        <Stack justifyContent="end">
          <Button variant="contained" color="primary" onClick={hideCopyModal}>
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default ExperienceOfferContentCopyModal;
